<template>
  <a-drawer
    :visible="visible"
    :title="$t('khqrPayment.detail.title')"
    placement="right"
    width="760"
    :zIndex="1021"
    @close="$emit('close')"
  >
    <ts-loading-banner :loading="loading">
      <div v-if="!attempt" class="khqr-empty-detail">
        {{ $t('khqrPayment.detail.empty') }}
      </div>

      <div v-else class="khqr-ref-drawer">
        <div class="khqr-ref-summary">
          <div class="khqr-ref-title-block">
            <div class="khqr-ref-label">{{ $t('khqrPayment.table.billNumber') }}</div>
            <div class="khqr-ref-bill">{{ attempt.bill_number || '-' }}</div>
            <div class="khqr-ref-chips">
              <span class="khqr-chip" :class="statusClass(attempt.status)">
                <i :class="statusIcon(attempt.status)"></i>
                {{ statusLabel(attempt.status) }}
              </span>
              <span class="khqr-chip khqr-chip-neutral">{{ providerLabel(attempt.provider) }}</span>
              <span class="khqr-chip khqr-chip-neutral">{{ sourceTypeLabel(attempt.source_type) }}</span>
              <span class="khqr-chip" :class="fulfillmentStatusClass(attempt.fulfillment_status)">
                {{ fulfillmentStatusLabel(attempt.fulfillment_status) }}
              </span>
            </div>
          </div>

          <div class="khqr-ref-amount-panel">
            <button
              v-if="canRetry"
              type="button"
              class="khqr-ref-retry-button"
              :disabled="retrying"
              @click="$emit('retry', attempt)"
            >
              <span>
                <i :class="retrying ? 'fas fa-spinner fa-spin' : 'fas fa-redo-alt'"></i>
              </span>
              <strong>{{ $t('khqrPayment.actions.retryFulfillment') }}</strong>
            </button>
            <div class="khqr-ref-amount-grid">
              <div>
                <span>{{ $t('khqrPayment.table.amount') }}</span>
                <strong>{{ formatAmount(attempt.amount, attempt.currency_symbol) }}</strong>
              </div>
              <div>
                <span>{{ $t('khqrPayment.table.currency') }}</span>
                <strong>{{ attempt.currency_code || '-' }}</strong>
              </div>
            </div>
          </div>
        </div>

        <div class="khqr-ref-body-grid">
          <div class="khqr-ref-left">
            <section class="khqr-ref-card">
              <header class="khqr-ref-card-head">
                <div class="khqr-ref-card-title">
                  <span><i class="fas fa-file-search"></i></span>
                  <h3>{{ $t('khqrPayment.detail.attemptOverview') }}</h3>
                </div>
                <div class="khqr-ref-card-actions">
                  <em>#{{ attempt.id }}</em>
                  <button type="button" @click="attemptOverviewCollapsed = !attemptOverviewCollapsed">
                    <i :class="attemptOverviewCollapsed ? 'fas fa-chevron-down' : 'fas fa-chevron-up'"></i>
                  </button>
                </div>
              </header>

              <div v-if="!attemptOverviewCollapsed" class="khqr-ref-overview-grid">
                <div class="khqr-ref-overview-col">
                  <div class="khqr-ref-info-row">
                    <span class="khqr-ref-icon sky"><i class="fas fa-university"></i></span>
                    <div>
                      <label>{{ $t('khqrPayment.table.bank') }}</label>
                      <strong>{{ attempt.bank_name || attempt.bank_name_kh || '-' }}</strong>
                    </div>
                  </div>
                  <div class="khqr-ref-info-row">
                    <span class="khqr-ref-icon indigo"><i class="fas fa-credit-card"></i></span>
                    <div>
                      <label>{{ $t('khqrPayment.table.accountName') }}</label>
                      <strong>{{ attempt.account_name || '-' }}</strong>
                      <small>{{ attempt.account_number || '-' }}</small>
                    </div>
                  </div>
                  <div class="khqr-ref-info-row">
                    <span class="khqr-ref-icon cyan"><i class="fas fa-sync-alt"></i></span>
                    <div>
                      <label>{{ $t('khqrPayment.table.providerStatus') }}</label>
                      <strong>{{ attempt.provider_status || '-' }}</strong>
                    </div>
                  </div>
                </div>

                <div class="khqr-ref-overview-col">
                  <div class="khqr-ref-info-row">
                    <span class="khqr-ref-icon violet"><i class="fas fa-link"></i></span>
                    <div>
                      <label>{{ $t('khqrPayment.table.transactionId') }}</label>
                      <strong>{{ attempt.provider_transaction_id || '-' }}</strong>
                    </div>
                  </div>
                  <div class="khqr-ref-info-row">
                    <span class="khqr-ref-icon slate"><i class="fas fa-code-branch"></i></span>
                    <div>
                      <label>{{ $t('khqrPayment.table.branch') }}</label>
                      <strong>{{ attempt.branch_name_en || attempt.branch_name_kh || '-' }}</strong>
                    </div>
                  </div>
                  <div class="khqr-ref-info-row">
                    <span class="khqr-ref-icon rose"><i class="fas fa-comment-dots"></i></span>
                    <div>
                      <label>{{ $t('khqrPayment.table.message') }}</label>
                      <p>{{ attempt.fulfillment_failure_message || attempt.failure_message || attempt.provider_message || '-' }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section class="khqr-ref-card khqr-ref-lifecycle">
              <div class="khqr-ref-section-title">
                <i class="fas fa-calendar-alt"></i>
                <h3>{{ $t('khqrPayment.detail.lifecycle') }}</h3>
              </div>
              <div class="khqr-ref-timeline">
                <div class="khqr-ref-time-row muted">
                  <span></span>
                  <div>
                    <strong>{{ $t('khqrPayment.detail.created') }}</strong>
                    <em>{{ formatDateTime(attempt.created_at) }}</em>
                    <small>{{ $t('khqrPayment.table.createdBy') }}: {{ attempt.created_by_name || attempt.created_by || '-' }}</small>
                  </div>
                </div>
                <div v-if="attempt.expires_at" class="khqr-ref-time-row warning">
                  <span></span>
                  <div>
                    <strong>{{ $t('khqrPayment.detail.expiresAt') }}</strong>
                    <em>{{ formatDateTime(attempt.expires_at) }}</em>
                  </div>
                </div>
                <div v-if="attempt.checked_at" class="khqr-ref-time-row info">
                  <span></span>
                  <div>
                    <strong>{{ $t('khqrPayment.detail.checkedAt') }}</strong>
                    <em>{{ formatDateTime(attempt.checked_at) }}</em>
                  </div>
                </div>
                <div v-if="attempt.paid_at" class="khqr-ref-time-row success">
                  <span></span>
                  <div>
                    <strong>{{ $t('khqrPayment.table.paidAt') }}</strong>
                    <em>{{ formatDateTime(attempt.paid_at) }}</em>
                  </div>
                </div>
                <div v-if="attempt.failed_at" class="khqr-ref-time-row danger">
                  <span></span>
                  <div>
                    <strong>{{ $t('khqrPayment.table.failedAt') }}</strong>
                    <em>{{ formatDateTime(attempt.failed_at) }}</em>
                    <small v-if="attempt.failure_code">{{ $t('khqrPayment.detail.failureCode') }}: {{ attempt.failure_code }}</small>
                  </div>
                </div>
              </div>
            </section>
          </div>

          <section class="khqr-ref-card khqr-ref-events">
            <header class="khqr-ref-events-head">
              <div class="khqr-ref-section-title">
                <i class="fas fa-clock"></i>
                <h3>{{ $t('khqrPayment.detail.events') }}</h3>
              </div>
              <span>{{ events.length }} {{ $t('khqrPayment.detail.records') }}</span>
            </header>

            <div v-if="initRequestEvent" class="khqr-ref-init-by">
              {{ $t('khqrPayment.detail.initBy') }}: <strong>{{ eventCreator(initRequestEvent) }}</strong>
            </div>

            <div v-if="!events.length" class="khqr-ref-no-events">
              {{ $t('khqrPayment.detail.noEvents') }}
            </div>

            <div v-else class="khqr-ref-events-grid">
              <div class="khqr-ref-event-list">
                <template v-for="event in events">
                  <button
                    :key="`event-${event.id}`"
                    type="button"
                    class="khqr-ref-event-button"
                    :class="{ active: isEventExpanded(event) }"
                    @click="toggleEvent(event)"
                  >
                    <div>
                      <strong>{{ eventTypeLabel(event.event_type) }}</strong>
                      <small>{{ formatDateTime(event.created_at) }}</small>
                      <small>{{ $t('khqrPayment.detail.createdBy') }}: {{ eventCreator(event) }}</small>
                    </div>
                    <div class="khqr-ref-event-actions">
                      <span :class="directionClass(event.direction)">{{ directionLabel(event.direction) }}</span>
                      <span class="khqr-ref-payload-action">
                        <i :class="isEventExpanded(event) ? 'fas fa-chevron-up' : 'fas fa-code'"></i>
                        {{ isEventExpanded(event) ? $t('khqrPayment.detail.hidePayload') : $t('khqrPayment.detail.viewPayload') }}
                      </span>
                    </div>
                    <em v-if="event.http_status !== null && event.http_status !== undefined">HTTP {{ event.http_status }}</em>
                  </button>

                  <div
                    v-if="isEventExpanded(event)"
                    :key="`payload-${event.id}`"
                    class="khqr-ref-event-detail"
                  >
                    <div class="khqr-ref-payload-head">
                      <div>
                        <i class="fas fa-code"></i>
                        <strong>{{ $t('khqrPayment.detail.payload') }}</strong>
                      </div>
                      <button type="button" @click.stop="toggleEvent(event)">
                        <i class="fas fa-chevron-up"></i>
                        {{ $t('khqrPayment.detail.hidePayload') }}
                      </button>
                    </div>
                    <div class="khqr-ref-event-meta-grid">
                      <div>
                        <label>{{ $t('khqrPayment.detail.eventType') }}</label>
                        <strong>{{ eventTypeLabel(event.event_type) }}</strong>
                      </div>
                      <div>
                        <label>{{ $t('khqrPayment.detail.httpStatus') }}</label>
                        <strong>{{ event.http_status !== null && event.http_status !== undefined ? event.http_status : '-' }}</strong>
                      </div>
                      <div>
                        <label>{{ $t('khqrPayment.detail.providerEventId') }}</label>
                        <strong>{{ event.provider_event_id || '-' }}</strong>
                      </div>
                      <div>
                        <label>{{ $t('khqrPayment.detail.createdBy') }}</label>
                        <strong>{{ eventCreator(event) }}</strong>
                      </div>
                    </div>
                    <pre>{{ eventPayload(event) }}</pre>
                  </div>
                </template>
              </div>
            </div>
          </section>
        </div>
      </div>
    </ts-loading-banner>
  </a-drawer>
</template>

<script>
export default {
  name: 'KhqrAttemptDetailDrawer',
  props: {
    visible: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    retrying: { type: Boolean, default: false },
    canRetryFulfillment: { type: Boolean, default: false },
    attempt: { type: Object, default: null },
    events: { type: Array, default: () => [] }
  },
  data() {
    return {
      activeEventId: null,
      attemptOverviewCollapsed: false
    }
  },
  computed: {
    initRequestEvent() {
      return this.events.find(event => event.event_type === 'init_request') || null
    },
    canRetry() {
      return this.canRetryFulfillment && this.attempt && this.attempt.status === 'paid' && this.attempt.fulfillment_status === 'failed'
    }
  },
  watch: {
    visible(value) {
      if (value) this.resetActiveEvent()
    },
    attempt() {
      this.attemptOverviewCollapsed = false
    },
    events() {
      this.resetActiveEvent()
    }
  },
  methods: {
    resetActiveEvent() {
      this.activeEventId = null
    },
    toggleEvent(event) {
      this.activeEventId = this.isEventExpanded(event) ? null : event.id
    },
    isEventExpanded(event) {
      return !!event && this.activeEventId === event.id
    },
    eventPayload(event) {
      if (!event) return '-'
      if (event.payload_pretty) return event.payload_pretty
      return this.formatPayload(event.payload)
    },
    eventCreator(event) {
      if (!event) return '-'
      return event.created_by_name || event.created_by || '-'
    },
    formatPayload(value) {
      if (!value) return '-'
      if (typeof value === 'string') return value
      try {
        return JSON.stringify(value, null, 2)
      } catch (e) {
        return String(value)
      }
    },
    formatAmount(value, symbol) {
      const amount = Number(value || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
      return `${symbol || '$'}${amount}`
    },
    formatDateTime(value) {
      if (!value) return '-'
      return String(value).replace('T', ' ').substring(0, 19)
    },
    providerLabel(value) {
      return this.translated(`khqrPayment.providers.${value}`, value)
    },
    sourceTypeLabel(value) {
      return this.translated(`khqrPayment.sourceTypes.${value}`, value)
    },
    statusLabel(value) {
      return this.translated(`khqrPayment.statuses.${value}`, value)
    },
    fulfillmentStatusLabel(value) {
      return this.translated(`khqrPayment.fulfillmentStatuses.${value || 'pending'}`, value || 'pending')
    },
    eventTypeLabel(value) {
      return this.translated(`khqrPayment.eventTypes.${value}`, value)
    },
    directionLabel(value) {
      return this.translated(`khqrPayment.directions.${value}`, value)
    },
    translated(key, fallback) {
      const text = this.$t(key)
      return text === key ? fallback || '-' : text
    },
    statusIcon(status) {
      return {
        paid: 'fas fa-check-circle',
        pending: 'fas fa-clock',
        failed: 'fas fa-times-circle'
      }[status] || 'fas fa-info-circle'
    },
    statusClass(status) {
      return {
        initiating: 'muted',
        pending: 'warning',
        paid: 'success',
        failed: 'danger',
        cancelled: 'muted'
      }[status] || 'muted'
    },
    fulfillmentStatusClass(status) {
      return {
        pending: 'muted',
        processing: 'info',
        completed: 'success',
        failed: 'danger',
        cancelled: 'muted'
      }[status || 'pending'] || 'muted'
    },
    directionClass(direction) {
      return direction === 'outbound' ? 'outbound' : direction === 'internal' ? 'internal' : 'inbound'
    }
  }
}
</script>

<style scoped>
.khqr-empty-detail,
.khqr-ref-no-events {
  padding: 28px;
  text-align: center;
  color: #64748b;
}
.khqr-ref-drawer {
  display: flex;
  flex-direction: column;
  gap: 12px;
  color: #0f172a;
}
.khqr-ref-summary,
.khqr-ref-card {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #fff;
}
.khqr-ref-summary {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  padding: 14px;
}
.khqr-ref-label,
.khqr-ref-amount-panel span,
.khqr-ref-info-row label,
.khqr-ref-event-meta-grid label {
  display: block;
  margin-bottom: 3px;
  color: #64748b;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
}
.khqr-ref-bill {
  font-size: 19px;
  font-weight: 700;
  overflow-wrap: anywhere;
}
.khqr-ref-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}
.khqr-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border-radius: 999px;
  padding: 2px 9px;
  font-size: 11px;
  font-weight: 700;
}
.khqr-chip-neutral,
.khqr-chip.muted {
  background: #f1f5f9;
  color: #475569;
  border: 1px solid #e2e8f0;
}
.khqr-chip.warning {
  background: #fffbeb;
  color: #b45309;
  border: 1px solid #fde68a;
}
.khqr-chip.success {
  background: #ecfdf5;
  color: #047857;
  border: 1px solid #a7f3d0;
}
.khqr-chip.danger {
  background: #fff1f2;
  color: #be123c;
  border: 1px solid #fecdd3;
}
.khqr-chip.info {
  background: #eff6ff;
  color: #1d4ed8;
  border: 1px solid #bfdbfe;
}
.khqr-ref-amount-panel {
  min-width: 230px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.khqr-ref-retry-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  min-height: 34px;
  border: 1px solid #f59e0b;
  border-radius: 8px;
  background: #fffbeb;
  color: #b45309;
  padding: 6px 11px;
  font-size: 12px;
  line-height: 1.2;
  cursor: pointer;
  transition: background-color .15s ease, border-color .15s ease, box-shadow .15s ease, transform .15s ease;
}
.khqr-ref-retry-button span {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  flex: 0 0 auto;
  border-radius: 999px;
  background: #f59e0b;
  color: #fff;
  font-size: 11px;
}
.khqr-ref-retry-button strong {
  font-weight: 700;
}
.khqr-ref-retry-button:hover:not(:disabled) {
  border-color: #d97706;
  background: #fef3c7;
  box-shadow: 0 6px 16px rgba(180, 83, 9, .14);
  transform: translateY(-1px);
}
.khqr-ref-retry-button:disabled {
  cursor: not-allowed;
  opacity: .75;
}
.khqr-ref-amount-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}
.khqr-ref-amount-grid div {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  padding: 9px;
}
.khqr-ref-amount-grid strong {
  display: block;
  font-size: 16px;
}
.khqr-ref-body-grid {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.khqr-ref-left {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.khqr-ref-card {
  overflow: hidden;
}
.khqr-ref-card-head,
.khqr-ref-events-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  border-bottom: 1px solid #f1f5f9;
  background: #f8fafc;
  padding: 9px 11px;
}
.khqr-ref-card-title,
.khqr-ref-section-title {
  display: flex;
  align-items: center;
  gap: 8px;
}
.khqr-ref-card-title span,
.khqr-ref-section-title i {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 27px;
  height: 27px;
  border-radius: 6px;
  background: #fff;
  color: #475569;
  box-shadow: 0 0 0 1px #e2e8f0;
}
.khqr-ref-card-title h3,
.khqr-ref-section-title h3 {
  margin: 0;
  font-size: 13px;
  font-weight: 700;
}
.khqr-ref-card-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}
.khqr-ref-card-actions em,
.khqr-ref-events-head span {
  border-radius: 999px;
  background: #fff;
  border: 1px solid #e2e8f0;
  padding: 2px 8px;
  color: #64748b;
  font-style: normal;
  font-size: 11px;
}
.khqr-ref-card-actions button {
  width: 27px;
  height: 27px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #64748b;
}
.khqr-ref-card-actions button:hover {
  background: #fff;
}
.khqr-ref-overview-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}
.khqr-ref-overview-col {
  display: grid;
  grid-template-rows: repeat(3, minmax(72px, 1fr));
}
.khqr-ref-overview-col + .khqr-ref-overview-col {
  border-left: 1px solid #f1f5f9;
  border-top: 0;
}
.khqr-ref-info-row {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 72px;
  padding: 10px 11px;
  border-bottom: 1px solid #f1f5f9;
}
.khqr-ref-info-row:last-child {
  border-bottom: 0;
}
.khqr-ref-icon {
  width: 25px;
  height: 25px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  border-radius: 6px;
  font-size: 12px;
}
.khqr-ref-icon.sky { background: #eff6ff; color: #2563eb; }
.khqr-ref-icon.indigo { background: #eef2ff; color: #4f46e5; }
.khqr-ref-icon.cyan { background: #ecfeff; color: #0891b2; }
.khqr-ref-icon.violet { background: #f5f3ff; color: #7c3aed; }
.khqr-ref-icon.slate { background: #f1f5f9; color: #475569; }
.khqr-ref-icon.rose { background: #fff1f2; color: #e11d48; }
.khqr-ref-info-row div {
  min-width: 0;
}
.khqr-ref-info-row strong,
.khqr-ref-info-row small,
.khqr-ref-info-row p {
  display: block;
  margin: 0;
  overflow-wrap: anywhere;
}
.khqr-ref-info-row strong {
  font-size: 13px;
}
.khqr-ref-info-row small,
.khqr-ref-info-row p {
  color: #64748b;
  font-size: 12px;
}
.khqr-ref-lifecycle {
  padding: 14px;
}
.khqr-ref-lifecycle .khqr-ref-section-title {
  margin-bottom: 10px;
}
.khqr-ref-timeline {
  display: flex;
  flex-direction: column;
  gap: 9px;
}
.khqr-ref-time-row {
  display: flex;
  gap: 10px;
  border-bottom: 1px dashed #e2e8f0;
  padding-bottom: 9px;
}
.khqr-ref-time-row:last-child {
  border-bottom: 0;
  padding-bottom: 0;
}
.khqr-ref-time-row > span {
  width: 8px;
  height: 8px;
  margin-top: 6px;
  border-radius: 999px;
  background: #94a3b8;
}
.khqr-ref-time-row.warning > span { background: #f59e0b; }
.khqr-ref-time-row.info > span { background: #38bdf8; }
.khqr-ref-time-row.success > span { background: #10b981; }
.khqr-ref-time-row.danger > span { background: #f43f5e; }
.khqr-ref-time-row div {
  flex: 1;
  min-width: 0;
}
.khqr-ref-time-row strong,
.khqr-ref-time-row em,
.khqr-ref-time-row small {
  display: block;
}
.khqr-ref-time-row strong {
  font-size: 13px;
}
.khqr-ref-time-row em,
.khqr-ref-time-row small {
  color: #64748b;
  font-style: normal;
  font-size: 11px;
}
.khqr-ref-events {
  padding-bottom: 12px;
}
.khqr-ref-events-head {
  border-bottom: 0;
}
.khqr-ref-init-by {
  margin: 0 12px 10px;
  border: 1px solid #bfdbfe;
  background: #eff6ff;
  border-radius: 8px;
  padding: 7px 9px;
  color: #1d4ed8;
  font-size: 12px;
}
.khqr-ref-events-grid {
  display: block;
  padding: 0 17px;
}
.khqr-ref-event-list {
  max-height: none;
  overflow: visible;
  padding-right: 0;
}
.khqr-ref-event-button {
  width: 100%;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 10px;
  position: relative;
  margin-bottom: 8px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  padding: 12px;
  text-align: left;
  cursor: pointer;
  transition: border-color .15s ease, background-color .15s ease, box-shadow .15s ease;
}
.khqr-ref-event-button:hover {
  border-color: #93c5fd;
  box-shadow: 0 4px 14px rgba(15, 23, 42, .06);
}
.khqr-ref-event-button.active {
  border-color: #bfdbfe;
  background: #eff6ff;
}
.khqr-ref-event-button strong,
.khqr-ref-event-button small,
.khqr-ref-event-button em {
  display: block;
}
.khqr-ref-event-button strong {
  font-size: 12px;
}
.khqr-ref-event-button small,
.khqr-ref-event-button em {
  grid-column: 1 / -1;
  color: #64748b;
  font-size: 11px;
  font-style: normal;
}
.khqr-ref-event-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
}
.khqr-ref-event-actions span {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  padding: 2px 7px;
  font-size: 10px;
  font-weight: 700;
  white-space: nowrap;
}
.khqr-ref-event-actions .khqr-ref-payload-action {
  gap: 5px;
  border: 1px solid #bfdbfe;
  background: #fff;
  color: #2563eb;
}
.khqr-ref-event-button.active .khqr-ref-payload-action {
  border-color: #93c5fd;
  background: #dbeafe;
}
.khqr-ref-event-actions span.outbound { background: #eff6ff; color: #2563eb; }
.khqr-ref-event-actions span.inbound { background: #f5f3ff; color: #7c3aed; }
.khqr-ref-event-actions span.internal { background: #ecfdf5; color: #047857; }
.khqr-ref-event-detail {
  margin: -2px 0 12px;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  background: #f8fbff;
  padding: 10px;
  box-shadow: inset 3px 0 0 #3b82f6;
}
.khqr-ref-payload-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 10px;
}
.khqr-ref-payload-head div,
.khqr-ref-payload-head button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.khqr-ref-payload-head div {
  color: #0f172a;
  font-size: 12px;
}
.khqr-ref-payload-head div i {
  color: #2563eb;
}
.khqr-ref-payload-head button {
  border: 1px solid #bfdbfe;
  border-radius: 6px;
  background: #fff;
  color: #2563eb;
  padding: 4px 8px;
  font-size: 11px;
  font-weight: 700;
}
.khqr-ref-event-meta-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  margin-bottom: 10px;
}
.khqr-ref-event-meta-grid div {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  padding: 8px;
  min-width: 0;
}
.khqr-ref-event-meta-grid strong {
  display: block;
  overflow-wrap: anywhere;
  font-size: 12px;
}
.khqr-ref-payload-label {
  display: block;
  margin-bottom: 6px;
  color: #64748b;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
}
.khqr-ref-event-detail pre {
  margin: 0;
  min-height: 180px;
  max-height: 360px;
  overflow: auto;
  border-radius: 8px;
  background: #0f172a;
  color: #e2e8f0;
  padding: 12px;
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-word;
}
@media (max-width: 900px) {
  .khqr-ref-summary,
  .khqr-ref-body-grid,
  .khqr-ref-events-grid,
  .khqr-ref-overview-grid,
  .khqr-ref-event-meta-grid {
    grid-template-columns: 1fr;
    display: grid;
  }
  .khqr-ref-overview-col + .khqr-ref-overview-col {
    border-left: 0;
    border-top: 1px solid #f1f5f9;
  }
  .khqr-ref-amount-panel {
    min-width: 0;
  }
}
</style>
